import React from 'react';
import CallLink from '../../../common/CallLink';
import PageLoader from '../../../common/PageLoader';
import { applySort, SORT, useUserCoords } from '../../../../utils/sortListings';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import 'bootstrap/dist/css/bootstrap.css';
import { getTaxiOwnerAllListing } from './../../../../Redux/Actions/UserAction';
import { map } from 'jquery';
import MapModalForView from '../../../common/Modal/MapModalForView';
import { useTranslation } from 'react-i18next';
import SortControl from '../../../common/SortControl';
import ServiceFilterPanel, {
  EMPTY_SERVICE_FILTER,
  isServiceFilterActive,
  serviceMatchesFilter,
  brandOptionsFromRows,
  vehicleTypeOptionsFromRows,
} from '../../../common/ServiceFilterPanel';

export default function UserTaxi() {
  const { t, i18n } = useTranslation();
  /* [CF 2026-08-17] The brand shown here came from `carDetails.name`, which is
     the English spelling, so an Arabic visitor read BMW / AUDI / LAND ROVER. The
     API now returns `nameAr` beside it (taxiOwner + towingOwner services), so
     pick whichever matches the language. */
  const isRTLBrand = (i18n.language || 'en').toLowerCase().startsWith('ar');
  const brandName = (cd) =>
    (isRTLBrand ? cd?.nameAr || cd?.name : cd?.name || cd?.nameAr) || '';
  const isRTL = (i18n.language || 'en').toLowerCase().startsWith('ar');
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getTaxiOwnerAllListing());
  }, [dispatch]);

  const home = useSelector((state) => state?.home);
  const getTaxiOwnerList = home?.getTaxiOwnerList;

  console.log('getTaxiOwnerList', getTaxiOwnerList);

  const [sortOption, setSortOption] = useState(SORT.DISTANCE); // [CF 2026-08-02] nearest-first by default
  // [CF 2026-08-02] Only asks the browser for a location when the user
  // actually picks the distance sort; cached for the session after that.
  const userCoords = useUserCoords(sortOption === SORT.DISTANCE);
  // [CF 2026-08-02] Brand comes from the row's `car` id, labelled from
  // `carDetails.name`; vehicle type from whatever owners have actually set.
  const [svcFilter, setSvcFilter] = useState({ ...EMPTY_SERVICE_FILTER });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [mapModalOpen, setMapModalOpen] = useState(false);

  const handleOpenMap = () => {
    setMapModalOpen(true);
  };

  const handleCloseMap = () => {
    setMapModalOpen(false);
  };

  // [CF 2026-08-11] Client: "Taxi shows the username in website not the company's
  // name." This preferred the ACCOUNT holder's first/last name, which for taxi
  // owners is the login handle — the cards read "taxi1808055", "doroataxi",
  // "Britishtaxi" instead of تاكسي الباشا / تاكسي دروع الخليج / تاكسي البريطاني.
  // The iOS app has always shown `taxiCompanyName`, and all 10 live taxis have it
  // populated, so prefer it and keep the account name only as a last resort.
  // Used for the card title, the search filter AND the A-Z sort, so all three
  // follow the company name together.
  const getTaxiName = (x) =>
    (x?.taxiCompanyName || '').trim() ||
    `${x?.user?.firstName || ''} ${x?.user?.lastName || ''}`.trim();

  const visibleList = React.useMemo(() => {
    if (!getTaxiOwnerList) return undefined;
    let list = [...getTaxiOwnerList];
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((x) => getTaxiName(x).toLowerCase().includes(q));
    }
    list = list.filter((x) => serviceMatchesFilter(x, svcFilter));
    list = applySort(list, sortOption, {
      name: getTaxiName,
      coords: userCoords,
    });
    return list;
  }, [getTaxiOwnerList, sortOption, userCoords, search, svcFilter]);

  const brandOptions = React.useMemo(
    () => brandOptionsFromRows(getTaxiOwnerList, isRTL),
    [getTaxiOwnerList, isRTL]
  );
  const vehicleTypeOptions = React.useMemo(
    () => vehicleTypeOptionsFromRows(getTaxiOwnerList, isRTL),
    [getTaxiOwnerList, isRTL]
  );

  /* [CF 2026-08-17] Taxi had NO loading state — it rendered an empty grid until
     the rows arrived and then popped them in. It already imported a loader
     without ever using one. Now uses the shared PageLoader. */
  if (getTaxiOwnerList === undefined) {
    return (
      <div className="text-center text-warning" style={{ padding: '80px' }}>
        <PageLoader />
      </div>
    );
  }

  // If no car details found after loading
  if (getTaxiOwnerList?.length === 0) {
    return (
      <div
        className="text-center"
        style={{
          padding: '50px',
        }}
      >
        <h4>{t('No Taxi found')}</h4>
        <p>{t('No Taxi is available right now. Please visit later')}</p>
      </div>
    );
  }

  return (
    <div>
      <div class="main-content-area">
        <div class="container">
          <h2 class="mb-2">{t('Taxi')}</h2>

          <div
            className="filter-row mb-4 d-flex align-items-center flex-nowrap"
            style={{ gap: '14px' }}
          >
            {/* Sort By — left */}
            <SortControl value={sortOption} onChange={setSortOption} />

            {/* [CF 2026-08-02] Filter funnel — panel opens on its own row below. */}
            <button
              type="button"
              onClick={() => setFiltersOpen((o) => !o)}
              aria-label={t('Filter')}
              title={t('Filter')}
              style={{
                width: '42px',
                height: '42px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                borderRadius: '50%',
                background: isServiceFilterActive(svcFilter) ? '#fff7e6' : 'transparent',
                cursor: 'pointer',
                flex: '0 0 auto',
              }}
            >
              <img
                src="assets/images/icons/filter.png"
                alt={t('Filter')}
                style={{ width: '22px', height: '22px' }}
              />
            </button>

            {/* Search (icon → expands) + result count — fills remaining space on the same row */}
            <div
              className="d-flex align-items-center"
              style={{
                gap: '12px',
                minWidth: 0,
                flex: '1 1 auto',
                justifyContent: 'flex-end',
              }}
            >
              {searchOpen ? (
                <div
                  style={{
                    position: 'relative',
                    flex: '1 1 auto',
                    maxWidth: '460px',
                    minWidth: 0,
                  }}
                >
                  <input
                    type="text"
                    autoFocus
                    className="form-control"
                    placeholder={t('Search by name')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ paddingInlineEnd: '34px' }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setSearch('');
                      setSearchOpen(false);
                    }}
                    aria-label={t('Clear')}
                    style={{
                      position: 'absolute',
                      insetInlineEnd: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: '20px',
                      lineHeight: 1,
                      color: '#666',
                    }}
                  >
                    &times;
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setSearchOpen(true)}
                  aria-label={t('Search by name')}
                  style={{
                    width: '42px',
                    height: '42px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'none',
                    borderRadius: '50%',
                    background: 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <img src="assets/images/icons/search.png" alt={t('Search by name')} style={{ width: '22px', height: '22px' }} />
                </button>
              )}
            </div>
          </div>

          {filtersOpen && (
            <ServiceFilterPanel
              value={svcFilter}
              onChange={setSvcFilter}
              onApply={() => setFiltersOpen(false)}
              fields={['brand', 'vehicleType']}
              brandOptions={brandOptions}
              vehicleTypeOptions={vehicleTypeOptions}
            />
          )}

          {visibleList && visibleList.length === 0 ? (
            <p className="text-muted">{t('No results match your search.')}</p>
          ) : (
            <div class="row">
              {(visibleList || []).map((x, i) => {
                const logo = x?.businessMedia?.find(
                  (media) => media?.mediaType === 'LOGO'
                )?.media?.path;

                //Location Data
                const gpsData = x?.gps || '{}';
                const lat = parseFloat((gpsData || {}).lat);
                const lng = parseFloat((gpsData || {}).long);
                // getTaxiName already falls back through company name → account
                // name, so this is just it.
                const name = getTaxiName(x);
                return (
                  <div
                    key={x?.taxiOwnerId || i}
                    className="col-6 col-sm-6 col-md-4 col-lg-3 mb-4"
                  >
                    <div className="cav-towing-card">
                      <Link
                        to={`/user-taxi-details/${x?.taxiOwnerId}`}
                        className="cav-towing-avatar"
                      >
                        <img
                          src={logo || 'assets/images/user/taxi/user.jpg'}
                          alt={name || ''}
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'assets/images/user/taxi/user.jpg';
                          }}
                        />
                      </Link>

                      <Link
                        to={`/user-taxi-details/${x?.taxiOwnerId}`}
                        className="cav-towing-name"
                      >
                        {name}
                      </Link>

                      <div className="cav-towing-meta">
                        {brandName(x?.carDetails) ? `${brandName(x.carDetails)} · ` : ''}
                        {t('Plate')} #{x?.plateNumber}
                      </div>

                      <div className="cav-towing-actions">
                        <a
                          target="_blank"
                          rel="noreferrer"
                          title="WhatsApp"
                          href={`https://wa.me/${`${x?.whatsAppNumberCountryCode}${x?.whatsAppNumber}`.replace(/\D/g, '')}`}
                        >
                          <img
                            src="assets/images/icons/user/car-dealers/whatsapp.svg"
                            alt="WhatsApp"
                          />
                        </a>
                        <CallLink
                          target="_blank"
                          rel="noreferrer"
                          title="Call"
                          record={x}
                        >
                          <img
                            src="assets/images/icons/user/car-dealers/telephone.svg"
                            alt="Call"
                          />
                        </CallLink>
                        <a
                          href="javascript:void(0);"
                          role="button"
                          title="Map"
                          onClick={handleOpenMap}
                        >
                          <img
                            src="assets/images/icons/user/car-dealers/map.svg"
                            alt="Map"
                          />
                        </a>
                        <MapModalForView
                          open={mapModalOpen}
                          handleClose={handleCloseMap}
                          currentLocation={{ lat: lat, long: lng }}
                          address={x?.location}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
